import React, { useEffect, useState } from 'react';
import { useUser } from '../context/UserContext';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const OrdersHistory = () => {
  const { authFetch, updateUserBalance } = useUser();
  const { increase, clearCart, editOrder } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOrderId, setEditingOrderId] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await authFetch('/orders');
        setOrders(res || []);
      } catch (err) {
        console.error('Ошибка загрузки заказов:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [authFetch]);

  const handleCancel = async (orderId) => {
    if (!window.confirm('Вы уверены, что хотите отменить заказ?')) return;

    try {
      const res = await authFetch(`/orders/${orderId}/cancel`, { method: 'PATCH' });
      
      if (res && (res.order?.id || res.message)) {
        setOrders(prev =>
          prev.map(order =>
            order.id === orderId ? { ...order, isCancelled: true, ...(res.order || {}) } : order
          )
        );
        
        if (typeof res.updatedBalance === 'number') {
          console.log('OrdersHistory: Attempting to update balance with:', res.updatedBalance);
          updateUserBalance(res.updatedBalance);
        } else {
          console.warn('OrdersHistory: updatedBalance not found or not a number in response:', res);
        }

        alert(res.message || '✅ Заказ отменён');
      } else {
        throw new Error(res?.error || 'Не удалось отменить заказ (нет order.id или message в ответе)');
      }
    } catch (err) {
      console.error('Ошибка при отмене заказа:', err);
      alert(err.message || '❌ Ошибка при отмене заказа');
    }
  };

  const handleRepeat = (order) => {
    clearCart();
    order.items.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        increase({
          id: item.dishId,
          name: item.name,
          price: item.price,
          imageUrl: item.imageUrl,
        });
      }
    });
    alert('Блюда добавлены в корзину');
  };

  const handleEdit = async (order) => {
    try {
      setEditingOrderId(order.id);
      await editOrder(order.id);
      navigate('/my-order');
    } catch (error) {
      console.error('Ошибка при редактировании заказа:', error);
      alert('❌ Не удалось загрузить заказ для редактирования.');
    } finally {
      setEditingOrderId(null);
    }
  };

  const sortedOrders = [...orders].sort((a, b) => {
    if (a.isReady === b.isReady) {
      return new Date(b.createdAt) - new Date(a.createdAt);
    }
    return a.isReady ? 1 : -1;
  });

  if (loading) {
    return <div className="p-4 text-white text-center">Загрузка истории заказов...</div>;
  }

  if (!orders.length) {
    return (
      <div className="p-4 text-white text-center">
        <p className="text-lg">История заказов пуста 🕓</p>
        <button
          onClick={() => navigate('/user')}
          className="mt-4 text-blue-400 hover:text-blue-300 underline text-sm"
        >
          ← Вернуться в меню
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-screen-md mx-auto text-white pb-24">
      <button
        onClick={() => navigate('/user')}
        className="mb-4 text-sm text-blue-400 hover:text-blue-300 underline"
      >
        ← Вернуться в меню
      </button>

      <h2 className="text-2xl font-bold mb-6 text-center">📜 История заказов</h2>

      <div className="space-y-6">
        {sortedOrders.map((order, index) => {
          const now = new Date();
          const isAfter5PM = now.getHours() >= 17;
          
          const tomorrow = new Date();
          tomorrow.setDate(now.getDate() + 1);
          const tomorrowString = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
          
          const isOrderForTomorrow = order.deliveryDate === tomorrowString;

          const isLocked = isOrderForTomorrow && isAfter5PM;
          const disabledTooltip = isLocked ? 'Редактирование и отмена заказа на завтра недоступны после 17:00' : '';

          const deliveryDate = new Date(order.deliveryDate.replace(/-/g, '/'));

          const statusLabel = order.isCancelled
            ? '❌ Отменён'
            : order.isReady
            ? '✅ Доставлен'
            : '🟡 В работе';

          const statusColor = order.isCancelled
            ? 'text-red-400'
            : order.isReady
            ? 'text-green-400'
            : 'text-yellow-400';

          return (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="bg-gray-800 rounded-xl shadow-xl p-4 border border-gray-700 hover:border-gray-500 transition"
            >
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm text-gray-400">
                  Доставка на <span className="text-white font-medium">{deliveryDate.toLocaleDateString('ru-RU')}</span>
                </p>
                <p className={`text-xs font-semibold ${statusColor}`}>{statusLabel}</p>
              </div>

              <p className="text-sm text-white mb-1">
                Номер заказа: <span className="font-semibold">#{order.shortCode || order.id}</span>
              </p>

              <p className="text-sm text-gray-300 mb-1">
                Состав: <span className="text-white">{order.items.map((item) => `${item.name} × ${item.quantity}`).join(', ')}</span>
              </p>

              <p className="text-sm text-gray-300 mb-1">
                Сумма заказа: <span className="text-white font-semibold">{order.total !== undefined ? `${order.total} ${order.currency || 'руб.'}` : 'N/A'}</span>
              </p>

              {order.comment && (
                <p className="text-sm text-gray-300 mb-1">
                  Комментарий: <span className="italic text-white">{order.comment}</span>
                </p>
              )}

              <p className="text-sm text-gray-300 mb-1">
                Приборы: <span className="text-white font-semibold">{order.needsCutlery ? 'Да' : 'Нет'}</span>
              </p>
              
              <p className="text-sm text-gray-300 mb-1">
                Хлеб: <span className="text-white font-semibold">
                  {order.needsBread 
                    ? (order.breadType 
                        ? (order.breadType === 'white' ? 'Белый' : 'Черный') 
                        : 'Да (тип не указан)') 
                    : 'Нет'}
                </span>
              </p>

              <p className="text-sm text-gray-400 mb-2">
                Доставлен к: <span className="text-white font-medium">{deliveryDate.toLocaleDateString()}</span>
              </p>

              {!order.isCancelled && (
                <div className="flex gap-3 mt-3 flex-wrap">
                  <button
                    onClick={() => handleRepeat(order)}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-1.5 rounded-lg text-sm transition shadow"
                  >
                    🔁 Повторить
                  </button>

                  {!order.isReady && (
                    <>
                      <button
                        onClick={() => handleEdit(order)}
                        disabled={isLocked || editingOrderId === order.id}
                        title={disabledTooltip}
                        className={`px-4 py-1.5 rounded-lg text-sm transition shadow ${
                          isLocked || editingOrderId === order.id
                            ? 'bg-gray-500 cursor-not-allowed' 
                            : 'bg-blue-600 hover:bg-blue-500 text-white'
                        }`}
                      >
                        {editingOrderId === order.id ? (
                          <>
                            <svg className="animate-spin h-4 w-4 inline mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Загрузка...
                          </>
                        ) : (
                          <>✏️ Редактировать</>
                        )}
                      </button>
                      <button
                        onClick={() => handleCancel(order.id)}
                        disabled={isLocked}
                        title={disabledTooltip}
                        className={`px-4 py-1.5 rounded-lg text-sm transition shadow ${
                          isLocked 
                            ? 'bg-gray-500 cursor-not-allowed' 
                            : 'bg-red-600 hover:bg-red-500 text-white'
                        }`}
                      >
                        ❌ Отменить
                      </button>
                    </>
                  )}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default OrdersHistory;
